import React from 'react';
import { AlertCircle, TrendingDown, FileX, Frown } from 'lucide-react';

const ProblemSection = () => {
  const problems = [
    { icon: TrendingDown, text: 'Endless applying' },
    { icon: FileX, text: 'No callbacks' },
    { icon: AlertCircle, text: 'Résumé doubt' },
    { icon: Frown, text: 'Burnout and frustration' }
  ];

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-2 bg-red-100 text-red-800 rounded-full text-sm font-semibold mb-4">
            The Problem
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Application Fatigue is <span className="text-red-600">Real</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Today's job market is flooded with AI-generated applications. Employers receive hundreds—sometimes thousands—of submissions per role.
          </p>
        </div>

        <div className="bg-gradient-to-br from-gray-50 to-slate-100 rounded-2xl p-8 md:p-12 border border-gray-200">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center">
              Qualified professionals are getting overlooked, not because they aren't capable—but because their applications get buried.
            </h3>

            <div className="grid md:grid-cols-2 gap-6 mt-10">
              {problems.map((problem, index) => {
                const Icon = problem.icon;
                return (
                  <div
                    key={index}
                    className="flex items-center space-x-4 bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
                  >
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="text-red-600" size={24} />
                    </div>
                    <span className="text-lg font-medium text-gray-900">{problem.text}</span>
                  </div>
                );
              })}
            </div>

            <div className="mt-12 p-6 bg-teal-50 border-l-4 border-teal-700 rounded-r-xl">
              <p className="text-xl font-semibold text-gray-900 mb-2">
                You're not the problem!
              </p>
              <p className="text-gray-700 text-lg">
                More applications won't fix it. <span className="font-bold text-teal-700">Better ones will.</span>
              </p>
            </div>
          </div>
        </div>

        {/* How We Help - 4 Step Process */}
        <div className="mt-20 max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Here's How We Help
            </h3>
            <p className="text-lg text-gray-600">
              A simple, proven process to get you noticed by hiring managers
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-teal-200 hover:shadow-xl transition-shadow">
              <div className="flex items-start space-x-4">
                <div className="w-14 h-14 bg-teal-700 rounded-lg flex items-center justify-center text-white font-bold text-2xl flex-shrink-0">
                  1
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Choose Your Service</h4>
                  <p className="text-gray-600">Select the package that fits your needs</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-amber-200 hover:shadow-xl transition-shadow">
              <div className="flex items-start space-x-4">
                <div className="w-14 h-14 bg-amber-600 rounded-lg flex items-center justify-center text-white font-bold text-2xl flex-shrink-0">
                  2
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Complete Intake Survey</h4>
                  <p className="text-gray-600">Share your goals and preferences</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-blue-200 hover:shadow-xl transition-shadow">
              <div className="flex items-start space-x-4">
                <div className="w-14 h-14 bg-blue-700 rounded-lg flex items-center justify-center text-white font-bold text-2xl flex-shrink-0">
                  3
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">We Apply For You</h4>
                  <p className="text-gray-600">Professional applications submitted</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-green-200 hover:shadow-xl transition-shadow">
              <div className="flex items-start space-x-4">
                <div className="w-14 h-14 bg-green-700 rounded-lg flex items-center justify-center text-white font-bold text-2xl flex-shrink-0">
                  4
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Track & Follow-Up</h4>
                  <p className="text-gray-600">Stay informed with tracking dashboard</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;